import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  DAY_END_HOUR,
  DAY_START_HOUR,
  CccCalEvent,
  PT_WEEKDAY_SHORT,
  SLOT_MINUTES,
  formatTime,
  isSameDay,
  layoutEventsInDay,
  parseIsoLocal,
  toDateKey,
} from './cccCalTypes';

type WeekGridProps = {
  days: Date[];
  events: CccCalEvent[];
  today: Date;
  conflictIds?: Set<number>;
  onSelectEvent: (event: CccCalEvent, anchor: DOMRect) => void;
  onSelectRange: (start: Date, end: Date) => void;
};

const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR;
const MIN_DRAG_MINUTES = 30;
const MIN_HOUR_HEIGHT = 26;

const clampMinutes = (m: number): number => Math.max(0, Math.min(TOTAL_HOURS * 60, m));

const WeekGrid: React.FC<WeekGridProps> = ({
  days,
  events,
  today,
  conflictIds,
  onSelectEvent,
  onSelectRange,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const daysRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [hourHeight, setHourHeight] = useState<number>(MIN_HOUR_HEIGHT);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => {
      const available = el.clientHeight;
      if (available <= 0) return;
      const next = Math.max(MIN_HOUR_HEIGHT, available / TOTAL_HOURS);
      setHourHeight(next);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  const gridHeight = TOTAL_HOURS * hourHeight;
  const minutesFromOffset = (offsetY: number): number => {
    const total = (offsetY / hourHeight) * 60;
    return Math.round(total / SLOT_MINUTES) * SLOT_MINUTES;
  };
  const eventTop = (start: Date): number => {
    const minutes = start.getHours() * 60 + start.getMinutes();
    return (minutes / 60) * hourHeight;
  };
  const eventHeight = (start: Date, end: Date): number => {
    const duration = Math.max(15, (end.getTime() - start.getTime()) / 60000);
    return Math.max(28, (duration / 60) * hourHeight);
  };

  const [dragState, setDragState] = useState<
    | null
    | {
        dayIndex: number;
        startMinutes: number;
        endMinutes: number;
      }
  >(null);
  const [nowMinutes, setNowMinutes] = useState(
    () => new Date().getHours() * 60 + new Date().getMinutes(),
  );

  const isSingleDay = days.length === 1;

  const eventsByDay = useMemo(() => {
    const map: Record<string, CccCalEvent[]> = {};
    days.forEach((d) => {
      map[toDateKey(d)] = [];
    });
    events.forEach((ev) => {
      if (ev.status === 'cancelled') return;
      const key = toDateKey(parseIsoLocal(ev.starts_at));
      if (!(key in map)) return;
      map[key].push(ev);
    });
    return map;
  }, [days, events]);

  const laidOutByDay = useMemo(() => {
    const map: Record<string, ReturnType<typeof layoutEventsInDay>> = {};
    Object.entries(eventsByDay).forEach(([key, list]) => {
      map[key] = layoutEventsInDay(list);
    });
    return map;
  }, [eventsByDay]);

  const daysKey = days.map((d) => toDateKey(d)).join('|');
  const scrolledForKey = useRef<string | null>(null);
  const hasEvents = events.length > 0;
  const willOverflow = gridHeight > (scrollRef.current?.clientHeight || 0) + 1;

  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    if (!willOverflow) {
      scrollRef.current.scrollTop = 0;
      scrolledForKey.current = null;
      return;
    }
    if (scrolledForKey.current === daysKey && hasEvents) return;

    const visibleKeys = new Set(days.map((d) => toDateKey(d)));
    const visibleEventHours = events
      .filter((ev) => ev.status !== 'cancelled')
      .map((ev) => parseIsoLocal(ev.starts_at))
      .filter((d) => visibleKeys.has(toDateKey(d)))
      .map((d) => d.getHours());

    const now = new Date();
    const nowHour = now.getHours();
    const isTodayVisible = days.some((d) => isSameDay(d, today));

    const anchor = visibleEventHours.length > 0
      ? Math.min(...visibleEventHours)
      : isTodayVisible
        ? nowHour
        : 8;

    const target = Math.max(0, Math.min(DAY_END_HOUR - 6, anchor - 1));
    scrollRef.current.scrollTop = target * hourHeight;
    scrolledForKey.current = daysKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysKey, hasEvents, willOverflow, hourHeight]);

  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setNowMinutes(n.getHours() * 60 + n.getMinutes());
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleMouseDown = (dayIndex: number, e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('.grh-week-event')) return;
    e.preventDefault();
    const dayEl = daysRefs.current[dayIndex];
    if (!dayEl) return;
    const rect = dayEl.getBoundingClientRect();
    const startY = e.clientY - rect.top;
    const startMinutes = clampMinutes(minutesFromOffset(startY));
    setDragState({
      dayIndex,
      startMinutes,
      endMinutes: startMinutes + SLOT_MINUTES,
    });
  };

  useEffect(() => {
    if (!dragState) return;

    const onMove = (e: MouseEvent) => {
      const dayEl = daysRefs.current[dragState.dayIndex];
      if (!dayEl) return;
      const rect = dayEl.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const nextMinutes = clampMinutes(minutesFromOffset(y));
      setDragState((prev) => (prev ? { ...prev, endMinutes: nextMinutes } : prev));
    };

    const onUp = () => {
      setDragState((prev) => {
        if (!prev) return null;
        const day = days[prev.dayIndex];
        const [a, b] =
          prev.startMinutes <= prev.endMinutes
            ? [prev.startMinutes, prev.endMinutes]
            : [prev.endMinutes, prev.startMinutes];
        const startMins = a;
        const endMins = Math.max(a + MIN_DRAG_MINUTES, b);
        const start = new Date(day);
        start.setHours(0, 0, 0, 0);
        start.setMinutes(startMins);
        const end = new Date(day);
        end.setHours(0, 0, 0, 0);
        end.setMinutes(endMins);
        onSelectRange(start, end);
        return null;
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState, days, onSelectRange, hourHeight]);

  const dragPreviewFor = (dayIndex: number) => {
    if (!dragState || dragState.dayIndex !== dayIndex) return null;
    const [a, b] =
      dragState.startMinutes <= dragState.endMinutes
        ? [dragState.startMinutes, dragState.endMinutes]
        : [dragState.endMinutes, dragState.startMinutes];
    const startMins = a;
    const endMins = Math.max(a + MIN_DRAG_MINUTES, b);
    const top = (startMins / 60) * hourHeight;
    const height = Math.max(
      (MIN_DRAG_MINUTES / 60) * hourHeight,
      ((endMins - startMins) / 60) * hourHeight,
    );
    const startLabel = `${String(Math.floor(startMins / 60)).padStart(2, '0')}:${String(startMins % 60).padStart(2, '0')}`;
    const endLabel = `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`;
    return (
      <div className="grh-week-drag" style={{ top, height }}>
        <span>{startLabel} – {endLabel}</span>
      </div>
    );
  };

  const hourLabels = useMemo(
    () => Array.from({ length: TOTAL_HOURS }, (_, i) => DAY_START_HOUR + i),
    [],
  );

  const isTodayInDays = days.some((d) => isSameDay(d, today));

  return (
    <div className="grh-week">
      <div className={`grh-week-header ${isSingleDay ? 'is-single' : ''}`}>
        <div className="grh-week-gutter-head" aria-hidden="true" />
        {days.map((d) => {
          const isToday = isSameDay(d, today);
          const weekday = PT_WEEKDAY_SHORT[(d.getDay() + 6) % 7];
          return (
            <div key={toDateKey(d)} className={`grh-week-day-head${isToday ? ' is-today' : ''}`}>
              <span className="grh-week-day-weekday">{weekday}</span>
              <span className="grh-week-day-date">
                <span className={`grh-week-day-num${isToday ? ' is-today' : ''}`}>
                  {d.getDate()}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      <div className="grh-week-scroll" ref={scrollRef}>
        <div className={`grh-week-body ${isSingleDay ? 'is-single' : ''}`}>
          <div className="grh-week-gutter" style={{ height: gridHeight }}>
            {hourLabels.map((h) => (
              <div
                key={h}
                className="grh-week-hour-label"
                style={{ top: h * hourHeight - 7 }}
              >
                {h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          {days.map((day, dayIndex) => {
            const key = toDateKey(day);
            const isToday = isSameDay(day, today);
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            const laidOut = laidOutByDay[key] || [];
            return (
              <div
                key={key}
                ref={(el) => {
                  daysRefs.current[dayIndex] = el;
                }}
                className={[
                  'grh-week-day-col',
                  isToday ? 'is-today' : '',
                  isWeekend ? 'is-weekend' : '',
                ].filter(Boolean).join(' ')}
                style={{ height: gridHeight }}
                onMouseDown={(e) => handleMouseDown(dayIndex, e)}
              >
                {hourLabels.map((h) => (
                  <div
                    key={h}
                    className="grh-week-hour-line"
                    style={{ top: h * hourHeight }}
                  />
                ))}
                {hourLabels.map((h) => (
                  <div
                    key={`half-${h}`}
                    className="grh-week-half-line"
                    style={{ top: h * hourHeight + hourHeight / 2 }}
                  />
                ))}

                {isToday && (
                  <div
                    className="grh-week-now"
                    style={{ top: (nowMinutes / 60) * hourHeight }}
                    aria-hidden="true"
                  >
                    <span className="grh-week-now-dot" />
                  </div>
                )}

                {laidOut.map(({ event: ev, col, cols }) => {
                  const start = parseIsoLocal(ev.starts_at);
                  const end = parseIsoLocal(ev.ends_at);
                  const top = eventTop(start);
                  const height = eventHeight(start, end);
                  const widthPct = 100 / cols;
                  const density: 'xs' | 'sm' | 'md' | 'lg' =
                    height < 30 ? 'xs' : height < 46 ? 'sm' : height < 66 ? 'md' : 'lg';
                  const name = ev.title;
                  const phone = ev.subtitle || '';
                  const hasConflict = Boolean(conflictIds?.has(ev.id));
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      className={[
                        'grh-week-event',
                        `area-${ev.tone}`,
                        `density-${density}`,
                        hasConflict ? 'is-conflict' : '',
                      ].filter(Boolean).join(' ')}
                      style={{
                        top,
                        height,
                        left: `calc(${col * widthPct}% + 2px)`,
                        width: `calc(${widthPct}% - 4px)`,
                        zIndex: hasConflict ? 2 : 1,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectEvent(ev, (e.currentTarget as HTMLElement).getBoundingClientRect());
                      }}
                      title={`${hasConflict ? '⚠️ Conflito de horário · ' : ''}${name}${phone ? ` (${phone})` : ''} · ${formatTime(start)}–${formatTime(end)}`}
                    >
                      {hasConflict && (
                        <span className="grh-week-event-conflict-badge" aria-label="Conflito de horário">
                          ⚠
                        </span>
                      )}
                      {density === 'xs' ? (
                        <span className="grh-week-event-line">
                          <span className="grh-week-event-time">{formatTime(start)}</span>
                          <span className="grh-week-event-name">{name}</span>
                        </span>
                      ) : (
                        <>
                          {density !== 'sm' && (
                            <span className="grh-week-event-time">
                              {formatTime(start)} – {formatTime(end)}
                            </span>
                          )}
                          <span className="grh-week-event-name">{name}</span>
                          {phone && (
                            <span className="grh-week-event-phone">({phone})</span>
                          )}
                          {density === 'lg' && ev.ownerName && (
                            <span className="grh-week-event-sub">{ev.ownerName}</span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}

                {dragPreviewFor(dayIndex)}
              </div>
            );
          })}
        </div>
      </div>

      {!isTodayInDays && <div className="grh-week-now-hint" aria-hidden="true" />}
    </div>
  );
};

export default WeekGrid;
